import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from "@iconify/react";

function ForgotPassword() {
  const { forgotPassword, resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');

  const [step, setStep] = useState(1); // 1: 輸入email, 2: 輸入新密碼 (reset mode)
  const [formData, setFormData] = useState({
    email: '',
    resetToken: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check for oobCode on mount
  useEffect(() => {
    if (oobCode) {
      setStep(2);
      setFormData(prev => ({ ...prev, resetToken: oobCode }));
    }
  }, [oobCode]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setMessage('');
  };

  const handleSendResetLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    const result = await forgotPassword(formData.email);

    if (result.success) {
      setMessage("重置連結已發送到您的信箱，請查看郵件並點擊連結以重置密碼。");
      // Stay on step 1, but show success message.
      // Alternatively, could show a "Check your email" view.
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('新密碼與確認密碼不相符');
      setLoading(false);
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('密碼至少需要 6 個字元');
      setLoading(false);
      return;
    }

    // oobCode is passed as resetToken
    const result = await resetPassword(formData.email, formData.resetToken, formData.newPassword);

    if (result.success) {
      setMessage(result.message + ' - 請使用新密碼登入');
      setTimeout(() => {
        // Redirect to login or just reset state
        window.location.href = '/login';
      }, 3000);
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg-primary)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: "2rem 1rem",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          background: "var(--bg-primary)",
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
          maxWidth: "1000px",
          width: "100%",
          minHeight: "700px",
          border: "5px solid #fff",
        }}
      >
        {/* Left Side - Purple Gradient */}
        <div
          style={{
            flex: 1,
            background:
              "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
            padding: "3rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            color: "white",
            position: "relative",
            borderRadius: "24px",
          }}
        >
          <div>
            <p
              style={{
                fontSize: "0.9rem",
                opacity: 0.9,
                marginBottom: "1rem",
                fontWeight: "400",
              }}
            >
              Reset your password
            </p>
            <h1
              style={{
                fontSize: "2.5rem",
                fontWeight: "700",
                lineHeight: "1.2",
                margin: "0 0 2rem 0",
              }}
            >
              {step === 1 ? '忘記密碼？' : '設定新密碼'}
              <br />
              {step === 1 ? '讓我們幫助您' : '馬上重置'}
            </h1>
          </div>

          {/* Features Section */}
          <div>
            <p
              style={{
                fontSize: "0.9rem",
                opacity: 0.8,
                marginBottom: "1.5rem",
                fontWeight: "400",
              }}
            >
              安全保障
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <div
                style={{
                  opacity: 0.9,
                  fontSize: "1rem",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <Icon icon="mdi:shield-check" width="20" height="20" />
                安全的驗證流程
              </div>
              <div
                style={{
                  opacity: 0.9,
                  fontSize: "1rem",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <Icon icon="mdi:clock-outline" width="20" height="20" />
                連結時效性保護
              </div>
              <div
                style={{
                  opacity: 0.9,
                  fontSize: "1rem",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <Icon icon="mdi:lock-reset" width="20" height="20" />
                密碼加密保護
              </div>
              <div
                style={{
                  opacity: 0.9,
                  fontSize: "1rem",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <Icon icon="mdi:email-secure" width="20" height="20" />
                Email 安全通知
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div
          style={{
            flex: 1,
            padding: "3rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            maxWidth: "450px",
            minHeight: "690px",
            overflowY: "auto",
          }}
        >
          <div style={{ marginBottom: "2rem" }}>
            <h2
              style={{
                fontSize: "1.8rem",
                fontWeight: "700",
                color: "#1f2937",
                marginBottom: "0.5rem",
                margin: "0",
              }}
            >
              {step === 1 ? '忘記密碼' : '重置密碼'}
            </h2>
            <p
              style={{
                color: "#6b7280",
                fontSize: "0.9rem",
                margin: "0.5rem 0 0 0",
              }}
            >
              {step === 1
                ? '輸入您的 Email，我們會發送重置連結給您'
                : '輸入新密碼來重置您的密碼'
              }
            </p>
          </div>

          {/* 步驟一：輸入 Email */}
          {step === 1 && (
            <form onSubmit={handleSendResetLink}>
              {error && (
                <div
                  style={{
                    background: "var(--bg-primary)",
                    color: "#ef4444",
                    padding: "0.75rem",
                    borderRadius: "8px",
                    marginBottom: "1rem",
                    border: "1px solid #fee2e2",
                    fontSize: "0.85rem",
                  }}
                >
                  {error}
                </div>
              )}

              {message && (
                <div
                  style={{
                    background: "var(--bg-primary)",
                    color: "#059669",
                    padding: "0.75rem",
                    borderRadius: "8px",
                    marginBottom: "1rem",
                    border: "1px solid #d1fae5",
                    fontSize: "0.85rem",
                  }}
                >
                  {message}
                </div>
              )}

              <div style={{ marginBottom: "1.5rem" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "0.5rem",
                    color: "#374151",
                    fontSize: "0.85rem",
                  }}
                >
                  Email <span style={{ color: "#374151" }}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="請輸入您的 Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "0.9rem",
                    background: "var(--bg-primary)",
                    color: "#374151",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  background: loading ? "#9ca3af" : "#667eea",
                  border: "none",
                  color: "white",
                  padding: "0.75rem",
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  marginBottom: "1.5rem",
                  transition: "all 0.2s ease",
                  userSelect: "none",
                }}
              >
                {loading ? '發送中...' : '發送重置連結'}
              </button>

              <div
                style={{
                  textAlign: "center",
                  fontSize: "0.85rem",
                  marginBottom: "1.5rem",
                }}
              >
                <span style={{ color: "#6b7280" }}>記起密碼了？</span>
                <Link
                  to="/login"
                  style={{
                    color: "#667eea",
                    textDecoration: "none",
                    fontWeight: "600",
                    marginLeft: "0.5rem",
                  }}
                >
                  立即登入
                </Link>
              </div>
            </form>
          )}

          {/* 步驟二：輸入新密碼 (Redirected from Email Link) */}
          {step === 2 && (
            <form onSubmit={handleResetPassword}>
              {error && (
                <div
                  style={{
                    background: "var(--bg-primary)",
                    color: "#ef4444",
                    padding: "0.75rem",
                    borderRadius: "8px",
                    marginBottom: "1rem",
                    border: "1px solid #fee2e2",
                    fontSize: "0.85rem",
                  }}
                >
                  {error}
                </div>
              )}

              {message && (
                <div
                  style={{
                    background: "var(--bg-primary)",
                    color: "#059669",
                    padding: "0.75rem",
                    borderRadius: "8px",
                    marginBottom: "1rem",
                    border: "1px solid #d1fae5",
                    fontSize: "0.85rem",
                  }}
                >
                  {message}
                </div>
              )}

              {/* Password Field */}
              <div style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "0.5rem",
                    color: "#374151",
                    fontSize: "0.85rem",
                  }}
                >
                  新密碼 <span style={{ color: "#374151" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    placeholder="請輸入新密碼 (至少6個字元)"
                    value={formData.newPassword}
                    onChange={handleChange}
                    required
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      paddingRight: "3rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "0.9rem",
                      background: "var(--bg-primary)",
                      color: "#374151",
                      boxSizing: "border-box",
                    }}
                  />
                  <div
                    onClick={togglePasswordVisibility}
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#6b7280",
                      display: "flex",
                      userSelect: "none",
                      transition: "color 0.2s ease",
                    }}
                  >
                    <Icon
                      icon={showPassword ? "mdi:eye-off" : "mdi:eye"}
                      width="20"
                      height="20"
                    />
                  </div>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div style={{ marginBottom: "1.5rem" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "0.5rem",
                    color: "#374151",
                    fontSize: "0.85rem",
                  }}
                >
                  確認新密碼 <span style={{ color: "#374151" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="請再次輸入新密碼"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      paddingRight: "3rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "0.9rem",
                      background: "var(--bg-primary)",
                      color: "#374151",
                      boxSizing: "border-box",
                    }}
                  />
                  <div
                    onClick={toggleConfirmPasswordVisibility}
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#6b7280",
                      display: "flex",
                      userSelect: "none",
                      transition: "color 0.2s ease",
                    }}
                  >
                    <Icon
                      icon={showConfirmPassword ? "mdi:eye-off" : "mdi:eye"}
                      width="20"
                      height="20"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  background: loading ? "#9ca3af" : "#667eea",
                  border: "none",
                  color: "white",
                  padding: "0.75rem",
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  marginBottom: "1.5rem",
                  transition: "all 0.2s ease",
                  userSelect: "none",
                }}
              >
                {loading ? '重置中...' : '重置密碼'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;